import { Input, isDecimalAmount, MAX_DECIMAL_LENGTH, SettingsGroup, Switch } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { useFormContext, useWatch } from 'react-hook-form';
import { decimalOf, variantsPricedLikeProduct, type ProductFormValues } from '../product-form.ts';
import { formatMargin, marginOf } from '../product.ts';
import { EditorSectionCard } from './EditorSectionCard.tsx';
import styles from './ProductEditor.module.css';
import { useFieldError, usePriceError } from './sections.ts';

export type PricingSectionProps = { currency: string; canSeeCost: boolean };

const marginText = (price: string, cost: string, currency: string, locale: string): string | null => {
  const [selling, bought] = [decimalOf(price), decimalOf(cost)];
  if (!isDecimalAmount(selling) || !isDecimalAmount(bought)) return null;
  return formatMargin(marginOf({ amount: selling, currency }, { amount: bought, currency }), locale);
};

export const PricingSection = ({ currency, canSeeCost }: PricingSectionProps) => {
  const { t, i18n } = useTranslation();
  const { register, formState, getValues, setValue } = useFormContext<ProductFormValues>();
  const perVariantSwitch = register('perVariantPrice');
  const errorText = useFieldError();
  const priceError = usePriceError();
  const [price, cost, promo, perVariantPrice] = useWatch<
    ProductFormValues,
    ['price', 'cost', 'promo', 'perVariantPrice']
  >({ name: ['price', 'cost', 'promo', 'perVariantPrice'] });
  const margin = canSeeCost && !perVariantPrice ? marginText(price, cost, currency, i18n.language) : null;
  const moneyField = { inputMode: 'decimal', dir: 'ltr', maxLength: MAX_DECIMAL_LENGTH } as const;

  return (
    <EditorSectionCard section="pricing">
      <div className={styles['stack']}>
        <div className={styles['columns']}>
          {canSeeCost && !perVariantPrice ? (
            <Input
              label={t('productEditor.cost')}
              suffix={currency}
              helperText={t('productEditor.costHelp')}
              errorText={errorText(formState.errors.cost)}
              {...moneyField}
              {...register('cost')}
            />
          ) : null}
          {perVariantPrice ? null : (
            <Input
              label={t('productEditor.sellingPrice')}
              suffix={currency}
              required
              errorText={priceError(formState.errors.price, { price, cost })}
              {...moneyField}
              {...register('price')}
            />
          )}
        </div>
        {margin === null ? null : (
          <p className={styles['margin']} aria-live="polite">
            {t('productEditor.margin')} {margin}
          </p>
        )}
        <SettingsGroup>
          <Switch label={t('productEditor.promo')} {...register('promo')} />
          <Switch
            label={t('productEditor.perVariantPrice')}
            helperText={t('productEditor.perVariantPriceHelp')}
            {...perVariantSwitch}
            onChange={(event) => {
              if (event.target.checked) setValue('variants', variantsPricedLikeProduct(getValues()));
              void perVariantSwitch.onChange(event);
            }}
          />
        </SettingsGroup>
        {promo ? (
          <Input
            label={t('productEditor.regularPrice')}
            suffix={currency}
            helperText={t('productEditor.regularPriceHelp')}
            required
            errorText={errorText(formState.errors.regularPrice)}
            {...moneyField}
            {...register('regularPrice')}
          />
        ) : null}
      </div>
    </EditorSectionCard>
  );
};
